/**
 * Desktop Games Tonight — the shared vote board (INVENTORY-DESKTOP §3.4, §6.4;
 * prototype lines 185-248). Lane D3.
 *
 * SAME AGGREGATION AS MOBILE. Every number, tier, row label and sort arrives
 * pre-computed from `tonightVMDesktop` → `tonightVM` → `lib/logic.ts` (`agg`,
 * `sharedRowMeta`, `sharedRowGames`). Nothing is re-derived here. `r.show` is
 * hardcoded `true` now that every row is one of the four calls — the field
 * survives because upstream's row VM still emits it, not because anything ever
 * sets it false.
 *
 * THE THREE DESKTOP DIFFERENCES
 *
 *  1. **Rows wrap instead of scrolling.** Mobile's row was a header line above
 *     a horizontally-scrolling strip; desktop is a fixed 104px label rail
 *     BESIDE a wrapping capsule field (§1.4). There are no horizontal
 *     scrollers anywhere on desktop (§1.5) — hence no `data-hs`.
 *  2. **The vote chips are hover-revealed.** Mobile printed the per-user chip
 *     strip under every capsule; desktop hides it in an "EVERYONE'S CALL"
 *     overlay inside the cover that fades in on hover (§3.4). The fade is the
 *     `.d-hov-show` class from `styles/desktop.css`, which reproduces the DC
 *     runtime's `!important` precedence over the inline `opacity:0`. A chip
 *     carries one caller's tier letter and nothing else, so the letter alone
 *     would not say what `B` means — `tonightVMDesktop` adds the desktop-only
 *     `NAME — B · I COULD BE CONVINCED` tooltip that spells it out (§7.5).
 *  3. **The rail glyph is bigger** — 22px on every row against mobile's 14px
 *     (`SHARED_ROW_BIG_FS`; `tonightVM.mFs` is the mobile one). The two sizes
 *     the binary board's `L` and `X` rows took went with those rows.
 *
 * NOT A DRAG SURFACE (§5.1). The shared board is read-only: no `draggable`, no
 * `data-gid`, no drop targets. A capsule click opens Detail, which is the whole
 * of "tap for options" on this screen — there is no move sheet on desktop (§4).
 *
 * `r.cntTxt` is computed by the selector and deliberately NOT rendered: the
 * prototype leaves an empty div here (line 206) and prints it on Interests.
 */

import { PixelButton } from '../../components/PixelButton.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { useQueueArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { sharedRowBigFs } from '../../store/selectors.d3.ts';
import { tonightVMDesktop } from '../../store/selectors.desktop.ts';
import { roundChipVM } from '../../store/selectors.round.ts';
import BoardCapsule from '../components/BoardCapsule.tsx';
import FilterBar from '../components/FilterBar.tsx';
import { useSearchInputRef } from '../searchHotkey.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

type TonightVM = ReturnType<typeof tonightVMDesktop>;
type SharedRow = TonightVM['sharedRows'][number];
type SharedGame = SharedRow['games'][number];

/** One 116px capsule with the hover-revealed vote overlay (lines 210-226). */
function Capsule({ g }: { g: SharedGame }): JSX.Element {
  const openGame = useQd((s) => s.openGame);

  return (
    <BoardCapsule
      id={g.id}
      nameUp={g.nameUp}
      fall={g.fall}
      ini={g.ini}
      coverCands={g.coverCands}
      filt={g.filt}
      onClick={() => openGame(g.id)}
      style={{ width: 116, border: '1px solid var(--gt-border-2)', cursor: 'pointer' }}
      coverOverlay={
        <div
          className="d-hov-show"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4,
            padding: '8px 6px',
            background: 'linear-gradient(transparent 40%,rgba(5,5,8,.92))',
            opacity: 0,
            transition: 'opacity .15s ease',
          }}
        >
          <div style={{ fontFamily: MONO, fontSize: '7.5px', letterSpacing: '.08em', color: 'var(--gt-text-5)' }}>
            EVERYONE&apos;S CALL
          </div>
          <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {g.chips.map((c) => (
              <div
                key={c.key}
                data-tip={c.title}
                style={{
                  width: 15,
                  height: 15,
                  flex: 'none',
                  background: c.bg,
                  border: c.bord,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: MONO,
                  fontSize: 9,
                  fontWeight: 600,
                  color: c.clr,
                }}
              >
                {c.t}
              </div>
            ))}
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 7px' }}>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9,
            color: 'var(--gt-text-3)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {g.nameUp}
        </div>
        <div style={{ display: 'flex', gap: 6, fontFamily: MONO, fontSize: '8.5px', whiteSpace: 'nowrap' }}>
          <span style={{ color: 'var(--acc)' }}>{g.tallyIn}</span>
          {g.hasOut ? <span style={{ color: 'var(--acc2)' }}>{g.tallyOut}</span> : null}
        </div>
      </div>
    </BoardCapsule>
  );
}

export default function Tonight(): JSX.Element {
  const s = useQd();
  const v = tonightVMDesktop(s);
  const rc = roundChipVM(s);
  // `/` focuses whichever board box is mounted — this one, while TONIGHT is up
  // (DESIGN-CHANGELOG 2026-08-28; `desktop/searchHotkey.ts`).
  const bqRef = useSearchInputRef();

  // Same art fetch the other party-list screens run — see `screens/Tonight.tsx`.
  useQueueArt(s.list.map((e) => e.id));

  return (
    <div data-screen-label="Games tonight" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* WHO'S PLAYING + tfNote + Filter Bar — ONE row (mobile used three) --- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '50px 26px 0',
          fontFamily: MONO,
          fontSize: 10,
          color: 'var(--gt-muted)',
          flexWrap: 'wrap',
        }}
      >
        <span>WHO&apos;S PLAYING:</span>
        <span
          onClick={() => s.tfAll()}
          style={{
            border: '1px solid ' + v.tfAllBd,
            color: v.tfAllClr,
            background: v.tfAllBg,
            padding: '5px 9px',
            cursor: 'pointer',
            letterSpacing: '.05em',
          }}
        >
          ALL
        </span>
        {v.tfChips.map((u) => (
          <div
            key={u.key}
            onClick={() => s.tfToggle(u.key)}
            data-tip={u.title}
            style={{
              position: 'relative',
              width: 24,
              height: 24,
              background: u.bg,
              border: u.bd,
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: PIXEL,
              fontSize: 7,
              color: 'var(--gt-ink)',
              overflow: 'hidden',
              cursor: 'pointer',
              opacity: u.op,
              transition: 'opacity .12s ease',
            }}
          >
            {u.ini}
            {u.hasAv ? <div style={{ position: 'absolute', inset: 0, background: u.avBg }} /> : null}
          </div>
        ))}
        {v.hasTfNote ? <span style={{ color: 'var(--acc)' }}>{v.tfNote}</span> : null}
        <div style={{ flex: 1 }} />
        {/* The box sits immediately before + FILTERS and always in that order,
            so one row says what set you are looking at. */}
        <SearchBox
          value={v.bq}
          onChange={(e) => s.setBq('tonight', e.target.value)}
          onClear={() => s.setBq('tonight', '')}
          inputRef={bqRef}
          placeholder="SEARCH THE LIST…"
          w="210px"
          pfs={11}
          ifs={10.5}
          pad="6px 10px"
        />
        <FilterBar />
        {/* START GAME NIGHT ▸ exists only when no round does (3039); once one is
            up the header chip is the door in. design/GT Boards.dc.html. */}
        {rc.rStartOn ? <PixelButton label="START GAME NIGHT ▸" fs={8} pad="11px 14px" nowrap fit lh="1" onClick={() => void s.rStart()} /> : null}
      </div>

      {/* tier rows — label rail beside a WRAPPING capsule field ------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 26px 30px' }}>
        {/* The board opens by asking for what only you can supply. Absent at
            zero, so a fully-called board shows nothing at all. Chip-on pattern
            at panel scale — 1px --acc over --accSoft, no new tokens. Wraps
            rather than crushing the CTA at narrow desktop widths. */}
        {v.hasIntNudge ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 18,
              border: '1px solid var(--acc)',
              background: 'var(--accSoft)',
              padding: '13px 15px',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                flex: 1,
                minWidth: 250,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--gt-font-pixel)',
                  fontSize: 8,
                  color: 'var(--acc)',
                }}
              >
                {v.intNudgeHead}
              </div>
              <div
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: 9.5,
                  letterSpacing: '.05em',
                  color: 'var(--gt-text-5)',
                }}
              >
                {v.intNudgeSub}
              </div>
            </div>
            <PixelButton
              label={v.intNudgeCta}
              fs={8}
              pad="12px 15px"
              nowrap
              fit
              lh="1"
              onClick={() => s.openCallRun(v.intNudgeIds)}
            />
          </div>
        ) : null}
        {v.sharedRows.map((r) =>
          r.show ? (
            <div key={r.key} style={{ display: 'flex', border: '1px solid ' + r.bd, background: 'var(--gt-card)' }}>
              <div
                style={{
                  width: 104,
                  flex: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  background: r.labBg,
                  borderRight: '1px solid var(--gt-border)',
                  padding: '16px 8px',
                }}
              >
                <div
                  style={{
                    fontFamily: PIXEL,
                    fontSize: sharedRowBigFs(r.key),
                    color: r.bigClr,
                    textShadow: 'var(--gt-shadow-btn)',
                  }}
                >
                  {r.big}
                </div>
                <div
                  style={{
                    fontFamily: MONO,
                    fontSize: '8.5px',
                    letterSpacing: '.1em',
                    color: 'var(--gt-text-5)',
                    textAlign: 'center',
                  }}
                >
                  {r.sub}
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  padding: 13,
                  alignContent: 'flex-start',
                  alignItems: 'flex-start',
                }}
              >
                {r.games.map((g) => (
                  <Capsule key={g.key} g={g} />
                ))}
                {r.isEmpty ? (
                  <div
                    style={{
                      alignSelf: 'center',
                      fontFamily: MONO,
                      fontSize: 10,
                      color: 'var(--gt-ghost)',
                      padding: '8px 2px',
                    }}
                  >
                    NOTHING HERE YET
                  </div>
                ) : null}
              </div>
            </div>
          ) : null,
        )}

        {/* NEEDS CALLS — one line on desktop, a stacked panel on mobile ----- */}
        {v.hasWaiting ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: '1px dashed var(--gt-border-3)',
              background: 'var(--gt-panel)',
              padding: '11px 14px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontFamily: PIXEL, fontSize: 7, color: 'var(--gt-text-5)' }}>NEEDS CALLS</div>
            {v.waitGames.map((w) => (
              <span
                key={w.key}
                onClick={() => s.openGame(w.id)}
                style={{
                  fontFamily: MONO,
                  fontSize: '9.5px',
                  color: 'var(--gt-text-5)',
                  border: '1px solid var(--gt-border)',
                  padding: '5px 8px',
                  cursor: 'pointer',
                }}
              >
                {w.nameUp} <span style={{ color: 'var(--gt-faint)' }}>· {w.cntTxt}</span>
              </span>
            ))}
            <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)' }}>
              ROW = EVERYONE'S AVERAGE CALL (S3 · A2 · B1 · C0, ROUNDED) — A GAME WAITS HERE UNTIL HALF OF YOU HAVE CALLED IT
            </div>
          </div>
        ) : null}

        {v.tEmpty ? (
          <div
            style={{
              padding: '50px 26px',
              textAlign: 'center',
              fontFamily: MONO,
              fontSize: 12,
              color: 'var(--gt-muted)',
              lineHeight: 2,
            }}
          >
            {v.tEmptyTxt}
          </div>
        ) : null}
      </div>
    </div>
  );
}
